export type UUID = `${string}-${string}-${string}-${string}-${string}`;

/** The nil UUID (all zeros). The server returns this as a default empty value. */
export const NIL_UUID: UUID = '00000000-0000-0000-0000-000000000000';

/** Returns true if the UUID is the nil UUID (all zeros). */
export function isNilUUID(id?: UUID | null): boolean {
  return id === NIL_UUID;
}

/** Returns true if the UUID is absent (null/undefined) or the nil UUID (all zeros). */
export function isEmptyUUID(id?: UUID | null): boolean {
  return id == null || id === NIL_UUID;
}

/** Generate a new UUID (v4) using the platform crypto API */
export function newUUID(): UUID {
  return crypto.randomUUID() as UUID;
}

/**
 * Type guard for UUID-looking strings (simple RFC4122 v4-ish check).
 * This is intentionally permissive for runtime checks but prevents
 * obviously invalid values from being treated as UUID.
 */
export function isUUID(v: unknown): v is UUID {
  if (v === undefined || v === null) return false;
  const s = String(v);
  const re =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return re.test(s);
}

/**
 * Safely coerce a value into a `UUID` type or return undefined when invalid.
 * Accepts any input; empty strings, null, undefined or non-matching values return undefined.
 */
export function asUUID(v?: unknown): UUID {
  if (v === undefined || v === null) {
    throw new Error('Missing UUID value');
  }
  const s = String(v);
  if (s === '') {
    throw new Error('Empty UUID string');
  }
  if (!isUUID(s)) {
    throw new Error(`Invalid UUID: ${s}`);
  }
  return s as UUID;
}

/**
 * Returns true if the value looks like an ID — a UUID or a long hex hash.
 * Useful for display decisions (e.g. truncate vs wrap).
 */
export function isIdValue(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  if (isUUID(value)) return true;
  return /^[0-9a-f]{16,}$/i.test(value);
}

/**
 * Returns true if the value looks like an email address.
 * Useful for display decisions (e.g. truncate vs wrap).
 */
export function isEmailValue(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * Compute a name-based UUID (v5 / SHA-1) from namespace UUID and name string.
 * Matches Go's uuid.NewSHA1(namespace, []byte(name)) for cross-platform consistency.
 */
export async function uuidSha1(namespace: UUID, name: string): Promise<UUID> {
  const hex = namespace.replace(/-/g, '');
  const namespaceBytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    namespaceBytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  const nameBytes = new TextEncoder().encode(name);
  const data = new Uint8Array(namespaceBytes.length + nameBytes.length);
  data.set(namespaceBytes);
  data.set(nameBytes, namespaceBytes.length);
  const hash = await crypto.subtle.digest('SHA-1', data);
  const h = new Uint8Array(hash);
  h[6] = (h[6]! & 0x0f) | 0x50;
  h[8] = (h[8]! & 0x3f) | 0x80;
  const uuid = `${hexSlice(h, 0, 4)}-${hexSlice(h, 4, 6)}-${hexSlice(h, 6, 8)}-${hexSlice(h, 8, 10)}-${hexSlice(h, 10, 16)}`;
  return uuid as UUID;
}

function hexSlice(arr: Uint8Array, start: number, end: number): string {
  return Array.from(arr.slice(start, end))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** The leading eight characters of an ID, for display where no name resolves. */
export function shortUUID(id: string): string {
  return id.slice(0, 8);
}
